import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
	FieldExpression,
	LuceneExpression,
	parse as luceneParse,
	term,
	toString as luceneToString
} from 'lucene';
import { BehaviorSubject, firstValueFrom, map, Observable, Subject } from 'rxjs';

import { AUTH_SERVER_URL, ORDER_SERVER_URL } from '../../config';
import { Batch, FieldOfBatch } from '../../order/models/batch';
import { ShipToDetails } from '../../order/models/ship-to-details';
import { FieldOfOrderItem, OrderItem } from '../../order/models/order-item';
import { PaginatedResult } from '../../shared/models/paginated-result';
import { OrderBy, OrderByDirection } from '../../shared/utils/constant';
import { BatchesService } from '../../order/services/batches.service';
import { MillSelectorMill } from '../components/mill/mill-selector.component';
import {
	CallableOrderItem,
	CalledBatch,
	EcallDatacenter,
	EcallResponse,
	Ecalls,
	FieldOfCalledBatch,
	FieldOfEcalls,
	FieldOfEcallDatacenter,
	EcallOffCardResponse
} from '../models/ecall-off-model';
import { EcallOffShipToDetails } from '../../order/models/ecall-off-ship-to-details';

const GET_CALLED_BATCHES_URL = `${ ORDER_SERVER_URL }/v1/ecalls:calledbatches`;
const GET_CALLABLE_BATCHES_URL = `${ ORDER_SERVER_URL }/v5/datacenterBatches`;
const GET_CALLABLE_ORDER_ITEMS_URL = `${ ORDER_SERVER_URL }/v1/ecalls:callableorderitems`;
const GET_ECALL_OFF_URL = `${ ORDER_SERVER_URL }/v1/ecalls`;
const GET_BATCH_MIN_QUANTITY = `${ ORDER_SERVER_URL }/v1/ecalls`;
const SAVE_ECALL_URL = `${ ORDER_SERVER_URL }/v2/ecall:save`;
const GET_AUTH_URL = `${ AUTH_SERVER_URL }/v3/organizations`;
const GET_ECALL_OFF_DATACENTER_URL = `${ ORDER_SERVER_URL }/v1/ecalloffs`;
const GET_ECALLOFFS_URL = `${ ORDER_SERVER_URL }/v1/ecalloffs`;

interface IECallOffService {
	getCallableOrderItems(
		fields: readonly FieldOfOrderItem[],
		filter: LuceneExpression | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof OrderItem>[] | OrderBy<keyof OrderItem>,
		millCodes: string[]
	): Promise<PaginatedResult<OrderItem>>;

	getEcallCard(fields: readonly FieldOfEcalls[], ecallId: number): Promise<Ecalls>;

	getEcallMills(
		index: number,
		limit: number,
		orderBy: OrderBy<keyof Ecalls>[] | OrderBy<keyof Ecalls>,
		onlyDistinct: boolean
	): Observable<Batch[]>;

	getEcalls(
		fields: readonly FieldOfEcalls[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof Ecalls>[] | OrderBy<keyof Ecalls>,
		onlyDistinct: boolean,
		includePcall: boolean
	): Promise<PaginatedResult<Ecalls>>;

	getEcallsFollowUp(
		fields: readonly FieldOfEcallDatacenter[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof EcallDatacenter>[] | OrderBy<keyof EcallDatacenter>,
		onlyDistinct: boolean,
		includePcall: boolean
	): Promise<PaginatedResult<EcallDatacenter>>;

	getEcallFromSAP(
		fields: readonly FieldOfEcalls[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof Ecalls>[] | OrderBy<keyof Ecalls>,
		onlyDistinct: boolean
	): Promise<PaginatedResult<EcallDatacenter>>;

	getIcon(ecall: Ecalls | EcallDatacenter): { id: string; title: string };

	updateEcallData(ecalls: Ecalls[]): void;

	updateEcallDatacenterData(ecalls: EcallDatacenter[]): Promise<void>;

	saveEcall(ecall: EcallResponse): Observable<number>;

	getEcallsForOrderCard(
		supplierOrderNumber: string,
		supplierOrderItemNumber: string
	): Observable<EcallOffCardResponse[]>;

}

@Injectable({ providedIn: 'root' })
export class ECallOffService implements IECallOffService {
	static readonly MINIMUM_LABOR_DAYS = 2;

	currentSelectedBatchesStep1: Observable<Batch[]>;
	currentMills: Observable<MillSelectorMill[] | undefined>;
	currentCustCallOffRef: Observable<string>;
	unloadingRef: Observable<string>;
	currentselectedBatchesStep2: Observable<CalledBatch[]>;
	currentRddMode: Observable<string>;
	currentCopyTo: Observable<string>;
	currentEcallOffSearchFilter: Observable<string>;
	currentStep1CallableBatchesFilter: Observable<LuceneExpression | undefined>;
	shipToList: Observable<EcallOffShipToDetails[]>;
	selectedShipto: Observable<EcallOffShipToDetails>;
	nextClicked: Observable<void>;
	private millDescriptionsByCodePromise?: Promise<Map<string, string>>;
	private preserveSelectedBatchesOnNextStepsInit = false;

	private readonly nextClickedSubject = new Subject<void>();
	private readonly custCallOffRefSubject = new BehaviorSubject(<string>'');
	private readonly unloadingRefSubject = new BehaviorSubject(<string>'');
	private readonly rddModeSubject = new BehaviorSubject(<string>'');
	private readonly copyToSubject = new BehaviorSubject(<string>'');
	private readonly millsSubject = new BehaviorSubject(<MillSelectorMill[] | undefined>undefined);
	private readonly selectedBatchesStep1Subject = new BehaviorSubject(<Batch[]>[]);
	private readonly selectedBatchesStep2Subject = new BehaviorSubject(<CalledBatch[]>[]);
	private readonly ecallOffSearchFilterSubject = new BehaviorSubject(<string>'');
	private readonly step1CallableBatchesFilterSubject = new BehaviorSubject<LuceneExpression | undefined>(
		undefined
	);

	private step1CallableBatchesFilter?: LuceneExpression;

	private readonly listOfShipToSubject = new BehaviorSubject(<EcallOffShipToDetails[]>[]);
	private readonly selectedShiptoSubject = new BehaviorSubject<EcallOffShipToDetails>(
		{
			shipTo: '',
			shipToDescription: '',
			address: '',
			city: '',
			postalCode: '',
			country: '',
			allowChangeOfShipto: false,
			millCodes: []
		}
	);

	constructor(
		private readonly http: HttpClient,
		private readonly router: Router,
		private readonly batchesService: BatchesService,
	) {
		this.currentselectedBatchesStep2 = this.selectedBatchesStep2Subject.asObservable();
		this.currentSelectedBatchesStep1 = this.selectedBatchesStep1Subject.asObservable();
		this.currentCustCallOffRef = this.custCallOffRefSubject.asObservable();
		this.unloadingRef = this.unloadingRefSubject.asObservable();
		this.currentRddMode = this.rddModeSubject.asObservable();
		this.currentCopyTo = this.copyToSubject.asObservable();
		this.currentMills = this.millsSubject.asObservable();
		this.currentEcallOffSearchFilter = this.ecallOffSearchFilterSubject.asObservable();
		this.currentStep1CallableBatchesFilter = this.step1CallableBatchesFilterSubject.asObservable();
		this.shipToList = this.listOfShipToSubject.asObservable();
		this.selectedShipto = this.selectedShiptoSubject.asObservable();
		this.nextClicked = this.nextClickedSubject.asObservable();
	}

	getCalledBatches(fields: readonly FieldOfCalledBatch[], index: number, limit: number) {
		const url = new URL(GET_CALLED_BATCHES_URL);

		const params = url.searchParams;
		params.append('fields', fields.join(','));
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));

		return this.http.get<PaginatedResult<CalledBatch>>(url.toString());
	}

	getCallableBatches(
		fields: readonly FieldOfBatch[],
		filter: LuceneExpression | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof Batch>[] | OrderBy<keyof Batch>,
	) {
		const url = new URL(GET_CALLABLE_BATCHES_URL);
		const params = url.searchParams;
		params.append('fields', typeof fields === 'string' ? fields : fields.join(','));
		if (filter) {
			params.append('filter', luceneToString(filter));
		}
		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));
		return this.http.get<PaginatedResult<Batch>>(url.toString());
	}

	async getCallableOrderItems(
		fields: readonly FieldOfOrderItem[],
		filter: LuceneExpression | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof OrderItem>[] | OrderBy<keyof OrderItem>,
		millCodes: string[]
	): Promise<PaginatedResult<OrderItem>> {
		if (millCodes.length === 0) {
			return <PaginatedResult<OrderItem>>{
				count: 0,
				limit,
				page: index,
				results: []
			};
		}
		const url = new URL(GET_CALLABLE_ORDER_ITEMS_URL);
		const params = url.searchParams;
		params.append('fields', typeof fields === 'string' ? fields : fields.join(','));
		if (filter) {
			params.append('filter', luceneToString(filter));
		}
		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));
		for (const millCode of millCodes) {
			params.append('millCode', encodeURIComponent(millCode));
		}
		return firstValueFrom(this.http.get<PaginatedResult<OrderItem>>(url.toString())
			.pipe(map(this.mapOrderItemResults.bind(this))));
	}

	mapOrderItemResults(result: PaginatedResult<OrderItem>) {
		const newResult = { ...result };
		newResult.results = result.results.map(orderItem => ({
			...orderItem,
			id: this.getOrderItemId(orderItem)
		}));
		return newResult;
	}

	getOrderItemId(orderItem: OrderItem) {
		const base = orderItem.customerOrderNumber;
		if (base && orderItem.customerOrderItemNumber) {
			return `${ base }/${ orderItem.customerOrderItemNumber }`;
		}
		return base ?? '';
	}

	async getEcallFromSAP(
		fields: readonly FieldOfEcalls[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof Ecalls>[] | OrderBy<keyof Ecalls>,
		onlyDistinct: boolean
	): Promise<PaginatedResult<EcallDatacenter>> {
		const url = new URL(GET_ECALL_OFF_DATACENTER_URL);

		const params = url.searchParams;
		const datacenterFields = this.mapEcallsFieldsToDatacenterFields(fields);

		params.append('fields', datacenterFields.join(','));
		if (filter) {
			params.append('filter', this.mapFilterToDatacenter(filter));
		}
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));
		params.append('orderBy', this.mapOrderByToDatacenter(orderBy));
		params.append('distinct', String(onlyDistinct));
		return firstValueFrom(
			this.http.get<PaginatedResult<EcallDatacenter>>(url.toString())
		).then(result => {
			const sortedResults = [...result.results].sort((a, b) => {
				const aTime = new Date(a.callOffCreationTimestamp).getTime();
				const bTime = new Date(b.callOffCreationTimestamp).getTime();
				return bTime - aTime;
			});

			return {
				...result,
				results: sortedResults
			};
		});
	}

	async getEcalls(
		fields: readonly FieldOfEcalls[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof Ecalls>[] | OrderBy<keyof Ecalls>,
		onlyDistinct: boolean,
		includePcall: boolean
	) {
		const url = new URL(GET_ECALL_OFF_URL);

		const params = url.searchParams;
		params.append('fields', fields.join(','));
		if (filter) {
			params.append('filter', filter);
		}
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));
		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));
		params.append('distinct', String(onlyDistinct));
		params.append('includePcall', String(includePcall));
		return firstValueFrom(this.http.get<PaginatedResult<Ecalls>>(url.toString()));
	}

	async getEcallsFollowUp(
		fields: readonly FieldOfEcallDatacenter[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof EcallDatacenter>[] | OrderBy<keyof EcallDatacenter>,
		onlyDistinct: boolean,
		includePcall: boolean
	) {
		const url = new URL(GET_ECALL_OFF_DATACENTER_URL);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		if (filter) {
			params.append('filter', filter);
		}
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));
		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));
		params.append('distinct', String(onlyDistinct));
		params.append('includePcall', String(includePcall));
		return firstValueFrom(this.http.get<PaginatedResult<EcallDatacenter>>(url.toString()));
	}

	getEcallsForOrderCard(
		supplierOrderNumber: string,
		supplierOrderItemNumber: string,
	): Observable<EcallOffCardResponse[]> {
		const url = new URL(`${ GET_ECALLOFFS_URL }/${ encodeURIComponent(supplierOrderNumber) }
		/item/${ encodeURIComponent(supplierOrderItemNumber) }?fields=items`);
		return this.http.get<EcallOffCardResponse[]>(url.toString());
	}

	getEcallMills() {
		const url = new URL(`${ GET_ECALL_OFF_URL }:mills`);
		return this.http.get<Batch[]>(url.toString());
	}

	getMinBatchQty(
		supplierOrderNumbers: CallableOrderItem[]
	) {
		const url = new URL(`${ GET_BATCH_MIN_QUANTITY }:minimumbatchquantity`);
		const params = url.searchParams;
		for (const data of supplierOrderNumbers) {
			if (data.supplierOrderNumber && data.supplierOrderItemNumber) {
				params.append('id', `${ data.supplierOrderNumber }/${ data.supplierOrderItemNumber }`);
			}
		}
		return this.http.get<Map<string, number>>(url.toString());
	}

	async getEcallCard(
		fields: readonly FieldOfEcalls[],
		ecallId: number
	) {
		const url = new URL(`${ GET_ECALL_OFF_URL }/${ encodeURIComponent(ecallId) }`);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		return firstValueFrom(this.http.get<Ecalls>(url.toString()));
	}

	async getShipToDetails(organizationId: number) {
		const url = new URL(`${ GET_AUTH_URL }/${ organizationId }/transactional-shiptos`);
		return firstValueFrom(this.http.get<EcallOffShipToDetails[]>(url.toString()));
	}

	saveEcall(ecall: EcallResponse) {
		this.sanitizeCustomerCalloffReference(ecall.customerReference);
		const url = new URL(SAVE_ECALL_URL);

		return this.http.post<number>(url.toString(), ecall);
	}

	setSelectedBatches(selectedBatches: Batch[]) {
		this.selectedBatchesStep1Subject.next(selectedBatches);
	}

	markBatchCardRedirect() {
		this.preserveSelectedBatchesOnNextStepsInit = true;
	}

	consumeBatchCardRedirect() {
		const shouldPreserve = this.preserveSelectedBatchesOnNextStepsInit;
		this.preserveSelectedBatchesOnNextStepsInit = false;
		return shouldPreserve;
	}

	updateMills(mills: MillSelectorMill[] | undefined) {
		this.millsSubject.next(mills);
	}

	updateSelectedBatchesStep2(selectedBatches: CalledBatch[]) {
		this.selectedBatchesStep2Subject.next(selectedBatches);
	}

	updateSelectedShipTos(selectedShipTos: EcallOffShipToDetails[]) {
		this.listOfShipToSubject.next(selectedShipTos);
	}

	updateCustCallOffRef(custCallOffRef: string) {
		this.custCallOffRefSubject.next(custCallOffRef);
	}

	updateUnloadingRef(unloadingRef: string) {
		this.unloadingRefSubject.next(unloadingRef);
	}

	updateSelectedShipto(selectedShipto: EcallOffShipToDetails) {
		this.selectedShiptoSubject.next(selectedShipto);
	}

	updateRddMode(rddMode: string) {
		this.rddModeSubject.next(rddMode);
	}

	updateCopyTo(copyTo: string) {
		this.copyToSubject.next(copyTo);
	}

	updateEcallOffSearchFilter(ecallOffSearchFilter: string) {
		this.ecallOffSearchFilterSubject.next(ecallOffSearchFilter);
	}

	updateStep1CallableBatchesFilter(filter: LuceneExpression | undefined) {
		this.step1CallableBatchesFilter = filter;
		this.step1CallableBatchesFilterSubject.next(filter);
	}

	consumeStep1CallableBatchesFilter(): LuceneExpression | undefined {
		const filter = this.step1CallableBatchesFilter;
		this.step1CallableBatchesFilter = undefined;
		this.step1CallableBatchesFilterSubject.next(undefined);
		return filter;
	}

	getCurrentRoute(): string {
		return this.router.url;
	}

	triggerNext() {
		this.nextClickedSubject.next();
	}

	getIcon(ecall: Ecalls | EcallDatacenter) {
		const hasVolumes = ('ecallVolumes' in ecall && (ecall.ecallVolumes ?? []).length > 0)
			|| ('requestedVolumeConcat' in ecall && ecall.requestedVolumeConcat
				&& ecall.requestedVolumeConcat.trim() !== '');

		if (hasVolumes) {
			return {
				id: 'in_stock_by_volume',
				title: 'Call-Off by volume'
			};
		}
		return {
			id: 'in_stock',
			title: 'Call-Off by batch'
		};
	}

	updateEcallData(ecalls: Ecalls[]) {
		for (const ecall of ecalls) {
			ecall.quantitySum = 0;
			if (ecall.type === 'B') {
				if (ecall.ecallBatches && ecall.ecallBatches.length > 0) {
					for (const calledBatch of ecall.ecallBatches) {
						if (!calledBatch.cancellationDate && calledBatch.grossWeight) {
							ecall.quantitySum += calledBatch.grossWeight;
						}
						this.updateEcallMillDescription(ecall, calledBatch.millDescription);
					}
				}
			} else if (ecall.type === 'V') {
				if (ecall.ecallVolumes && ecall.ecallVolumes.length > 0) {
					for (const calledVolume of ecall.ecallVolumes) {
						if (calledVolume.requestedVolume) {
							ecall.quantitySum += calledVolume.requestedVolume;
						}
						this.updateEcallMillDescription(ecall, calledVolume.millDescription);
					}
				}
			} else {
				if (ecall.ecallBatches && ecall.ecallBatches.length > 0) {
					for (const calledBatch of ecall.ecallBatches) {
						if (!calledBatch.cancellationDate && calledBatch.grossWeight) {
							ecall.quantitySum += calledBatch.grossWeight;
						}
						this.updateEcallMillDescription(ecall, calledBatch.millDescription);
					}
				}
				if (ecall.ecallVolumes && ecall.ecallVolumes.length > 0) {
					for (const calledVolume of ecall.ecallVolumes) {
						if (calledVolume.requestedVolume) {
							ecall.quantitySum += calledVolume.requestedVolume;
						}
						this.updateEcallMillDescription(ecall, calledVolume.millDescription);
					}
				}
			}
		}
	}

	dateFilter = (dateToCheck: Date | null): boolean => {
		if (dateToCheck === null) {
			return false;
		}
		const minDate = this.addLaborDays(this.startOfDay(new Date()), ECallOffService.MINIMUM_LABOR_DAYS);
		return this.startOfDay(dateToCheck) >= minDate;
	};

	getMinToDate(date?: Date) {
		if (!date) {
			return null;
		}
		const minToDate = new Date(date);
		minToDate.setDate(minToDate.getDate() + 1);
		return minToDate;
	}

	getMaxFromDate(date?: Date) {
		if (!date) {
			return null;
		}
		const minToDate = new Date(date);
		minToDate.setDate(minToDate.getDate() - 1);
		return minToDate;
	}

	validateMillAndShipToCombination(
		currentShipToDetails?: ShipToDetails,
		millCode?: string,
	) {
		return !(!currentShipToDetails || (millCode && currentShipToDetails.millCodes
			&& !currentShipToDetails.millCodes.includes(millCode)));
	}

	async getEcallsOffCard(supplierCallOffNumber: string): Promise<EcallOffCardResponse> {
		const url = new URL(`${ GET_ECALLOFFS_URL }/${ encodeURIComponent(supplierCallOffNumber) }?fields=items`);
		return firstValueFrom(this.http.get<EcallOffCardResponse>(url.toString()));
	}

	async updateEcallDatacenterData(ecalls: EcallDatacenter[]) {
		const millDescriptionsByCode = await this.getMillDescriptionsByCode();
		const batchCallOffs = ecalls.filter(ecall => ecall.callOffType === 'B');
		const quantityByCallOff = new Map<string, number>();
		const fields: FieldOfBatch[] = ['qtyBatchGross'];
		const maxBatchPageSize = 1;
		const perCallOffPromises = batchCallOffs.map(async ecall => {
			const supplierCallOffNumber = (ecall.supplierCallOffNumber || '').trim();

			if (!supplierCallOffNumber) {
				quantityByCallOff.set(ecall.supplierCallOffNumber, 0);
				return;
			}

			const batchFilter = this.buildSupplierCallOffNumberFilter(supplierCallOffNumber);

			try {
				const batchResult = await firstValueFrom(
					this.batchesService.getDatacenterBatchesV5(
						fields,
						batchFilter,
						0,
						maxBatchPageSize,
						new OrderBy('qtyBatchGross', OrderByDirection.DESC),
						true
					)
				);

				const aggregatedQuantity = batchResult.results[0]?.qtyBatchGross ?? 0;
				quantityByCallOff.set(ecall.supplierCallOffNumber, aggregatedQuantity);
			} catch (error) {
				console.error('Error fetching batch data:', error);
				quantityByCallOff.set(ecall.supplierCallOffNumber, 0);
			}
		});

		await Promise.all(perCallOffPromises);

		for (const ecall of ecalls) {
			if (ecall.millConcat) {
				const millCodes = ecall.millConcat.split(';').map(m => m.trim()).filter(m => m !== '');
				const uniqueMillCodes = [...new Set(millCodes)];
				const uniqueMillDescriptions = Array.from(new Set(
					uniqueMillCodes.map(millCode => millDescriptionsByCode.get(millCode) ?? millCode)
				));
				ecall.millDescriptionConcat = uniqueMillDescriptions.join(';');
				const millDescriptionValue = uniqueMillCodes.length === 1
					? millDescriptionsByCode.get(uniqueMillCodes[0]) ?? uniqueMillCodes[0]
					: uniqueMillCodes.length > 1 ? '*' : undefined;
				ecall.millDescription = millDescriptionValue;
			}

			let quantitySum = 0;
			if (ecall.callOffType === 'B') {
				quantitySum = quantityByCallOff.get(ecall.supplierCallOffNumber) ?? 0;
			} else if ((ecall.callOffType === 'V' || ecall.callOffType === 'P') && ecall.requestedVolumeConcat) {
				const volumes = ecall.requestedVolumeConcat.split(';');
				for (const vol of volumes) {
					if (vol.trim() !== '') {
						quantitySum += Number(vol) || 0;
					}
				}
			}
			ecall.quantitySum = quantitySum;
		}
	}

	private async getMillDescriptionsByCode(): Promise<Map<string, string>> {
		if (!this.millDescriptionsByCodePromise) {
			this.millDescriptionsByCodePromise = firstValueFrom(this.getEcallMills())
				.then(mills => {
					const descriptionsByCode = new Map<string, string>();
					for (const mill of mills) {
						if (mill.mill && mill.millDescription) {
							descriptionsByCode.set(mill.mill, mill.millDescription);
						}
					}
					return descriptionsByCode;
				})
				.catch((error: unknown) => {
					console.error('Error fetching mill descriptions:', error);
					return new Map<string, string>();
				});
		}

		return this.millDescriptionsByCodePromise;
	}

	private startOfDay(date: Date): Date {
		const result = new Date(date);
		result.setHours(0, 0, 0, 0);
		return result;
	}

	private addLaborDays(date: Date, laborDays: number): Date {
		const result = new Date(date);
		let added = 0;
		while (added < laborDays) {
			result.setDate(result.getDate() + 1);
			const dayOfWeek = result.getDay();
			if (dayOfWeek !== 0 && dayOfWeek !== 6) {
				added++;
			}
		}
		return result;
	}

	private buildSupplierCallOffNumberFilter(supplierCallOffNumber: string): string {
		const singleFilter: FieldExpression = {
			field: 'supplierCallOffNumber',
			term: term.escape(supplierCallOffNumber),
			quoted: true
		};

		return luceneToString(singleFilter);
	}

	private updateEcallMillDescription(ecall: Ecalls, millDescription?: string) {
		if (!millDescription || millDescription.trim() === '') {
			return;
		}
		if (!ecall.millDescription) {
			ecall.millDescription = millDescription.trim();
		} else if (ecall.millDescription !== millDescription.trim()) {
			// case where a same ecall has batches/volumes of different mills
			ecall.millDescription = '*';
		}
	}

	private sanitizeCustomerCalloffReference(reference: string | undefined) {
		return reference && reference !== '' && reference.includes('\t')
			? reference.replaceAll('\t', ' ')
			: reference;
	}

	private mapEcallsFieldsToDatacenterFields(fields: readonly FieldOfEcalls[]): string[] {
		const fieldMapping = this.getEcallsToDatacenterFieldMapping();

		const datacenterFields = new Set<string>();
		datacenterFields.add('supplierCallOffNumber');
		datacenterFields.add('customerCallOffReference');
		datacenterFields.add('callOffCreationTimestamp');
		datacenterFields.add('callOffType');

		for (const field of fields) {
			const mappedField = fieldMapping.get(field);
			if (mappedField) {
				datacenterFields.add(mappedField);
			}
		}

		return Array.from(datacenterFields);
	}

	private mapFilterToDatacenter(filter: string): string {
		try {
			const expression = luceneParse(filter);
			const mappedExpression = this.mapFilterExpressionFieldsToDatacenter(expression);
			return luceneToString(mappedExpression);
		} catch (error) {
			console.warn('Unable to parse ecall filter, sending original filter', error);
			return filter;
		}
	}

	private mapFilterExpressionFieldsToDatacenter(expression: LuceneExpression): LuceneExpression {
		if ('field' in expression) {
			const sourceField = expression.field ?? '';
			const mappedField = this.getEcallsToDatacenterFieldMapping().get(sourceField) ?? sourceField;

			if ('term_min' in expression || 'term_max' in expression) {
				const mappedRangeExpression = {
					...expression,
					field: mappedField
				};
				const rangeExpression = <{
					[key: string]: unknown;
					field: string;
					term_min?: unknown;
					term_max?: unknown;
				}>mappedRangeExpression;

				if (mappedField === 'callOffCreationTimestamp') {
					const termMin = typeof rangeExpression.term_min === 'string'
						? rangeExpression.term_min
						: '*';
					const termMax = typeof rangeExpression.term_max === 'string'
						? rangeExpression.term_max
						: '*';

					return <LuceneExpression>{
						...mappedRangeExpression,
						term_min: this.toStartOfDayDateTime(termMin),
						term_max: this.toEndOfDayDateTime(termMax)
					};
				}

				return <LuceneExpression><unknown>mappedRangeExpression;
			}

			return {
				...expression,
				field: mappedField
			};
		}

		if ('left' in expression && 'right' in expression && expression.right) {
			return {
				...expression,
				left: this.mapFilterExpressionFieldsToDatacenter(expression.left),
				right: this.mapFilterExpressionFieldsToDatacenter(expression.right)
			};
		}

		if ('left' in expression) {
			return {
				...expression,
				left: this.mapFilterExpressionFieldsToDatacenter(expression.left)
			};
		}

		return expression;
	}

	private toStartOfDayDateTime(value: string) {
		if (value === '*') {
			return value;
		}

		const datePattern = /^(\d{4}-\d{2}-\d{2})(Z|[+-]\d{2}:\d{2})?$/;
		const dateMatch = datePattern.exec(value);
		if (!dateMatch) {
			return value;
		}

		const [, datePart, zonePart] = dateMatch;
		const zone = zonePart || 'Z';
		return `${ datePart }T00:00:00${ zone }`;
	}

	private toEndOfDayDateTime(value: string) {
		if (value === '*') {
			return value;
		}

		const datePattern = /^(\d{4}-\d{2}-\d{2})(Z|[+-]\d{2}:\d{2})?$/;
		const dateMatch = datePattern.exec(value);
		if (!dateMatch) {
			return value;
		}

		const [, datePart, zonePart] = dateMatch;
		const zone = zonePart || 'Z';
		return `${ datePart }T23:59:59${ zone }`;
	}

	private getEcallsToDatacenterFieldMapping(): Map<string, string> {
		return new Map<string, string>([
			['id', 'supplierCallOffNumber'],
			['type', 'callOffType'],
			['customerReference', 'customerCallOffReference'],
			['comment', 'comment'],
			['rowCreationDate', 'callOffCreationTimestamp'],
			['rowCreationBy', 'callOffCreationBy'],
			['personId', 'callOffCreatorId'],
			['callCreator', 'callOffCreator'],
			['rddMode', 'requestedDateForCallOffType'],
			['copyTo', 'copyTo'],
			['ecallBatches.id', 'supplierCallOffItemNumberConcat'],
			['ecallBatches.batchReference', 'batchReferenceConcat'],
			['ecallBatches.sapBatchId', 'supplierBatchReferenceConcat'],
			['ecallBatches.cancellationDate', 'batchCancellationTimestampConcat'],
			['ecallBatches.millCode', 'millConcat'],
			['ecallBatches.supplierOrderReference', 'supplierOrderNumberConcat'],
			['ecallBatches.supplierOrderItem', 'supplierOrderItemNumberConcat'],
			['ecallBatches.shipTo', 'callOffShipToConcat'],
			['ecallBatches.shipToDescription', 'callOffShipToDescriptionConcat'],
			['ecallBatches.originalShipTo', 'shipToConcat'],
			['ecallBatches.originalShipToDescription', 'shipToDescriptionConcat'],
			['ecallBatches.soldTo', 'soldToConcat'],
			['ecallBatches.soldToDescription', 'soldToDescriptionConcat'],
			['ecallBatches.requestedDeliveryDateFrom', 'requestedDeliveryDateFromConcat'],
			['ecallBatches.requestedDeliveryDateTo', 'requestedDeliveryDateToConcat'],
			['ecallBatches.unloadingReference', 'unloadingReferenceConcat'],
			['ecallBatches.comment', 'commentConcat'],
			['ecallBatches.rowCreationDate', 'callOffItemCreationTimestampConcat'],
			['ecallVolumes.id', 'supplierCallOffItemNumberConcat'],
			['ecallVolumes.requestedVolume', 'requestedVolumeConcat'],
			['ecallVolumes.requestedVolumeUnit', 'requestedVolumeUnitConcat'],
			['ecallVolumes.millCode', 'millConcat'],
			['ecallVolumes.supplierOrderReference', 'supplierOrderNumberConcat'],
			['ecallVolumes.supplierOrderItem', 'supplierOrderItemNumberConcat'],
			['ecallVolumes.shipTo', 'callOffShipToConcat'],
			['ecallVolumes.shipToDescription', 'callOffShipToDescriptionConcat'],
			['ecallVolumes.originalShipTo', 'shipToConcat'],
			['ecallVolumes.originalShipToDescription', 'shipToDescriptionConcat'],
			['ecallVolumes.requestedDeliveryDateFrom', 'requestedDeliveryDateFromConcat'],
			['ecallVolumes.requestedDeliveryDateTo', 'requestedDeliveryDateToConcat'],
			['ecallVolumes.unloadingReference', 'unloadingReferenceConcat'],
			['ecallVolumes.comment', 'commentConcat']
		]);
	}

	private mapOrderByToDatacenter(orderBy: OrderBy<keyof Ecalls>[] | OrderBy<keyof Ecalls>): string {
		const orderByArray = Array.isArray(orderBy) ? orderBy : [orderBy];
		const mappedOrders = orderByArray.map(order => {
			const orderStr = String(order);
			if (orderStr.includes('rowCreationDate')) {
				return orderStr.replace('rowCreationDate', 'callOffCreationTimestamp');
			}
			if (orderStr.includes('customerReference')) {
				return orderStr.replace('customerReference', 'customerCallOffReference');
			}
			if (orderStr.includes('id')) {
				return orderStr.replace('id', 'supplierCallOffNumber');
			}
			return orderStr;
		});
		return mappedOrders.length > 0 ? mappedOrders.join(',') : 'callOffCreationTimestamp desc';
	}
}
